/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import { FC } from 'react';
import Button from '~/components/commonUi/Button/Button';
import Fieldset from '~/components/commonUi/Fieldset';
import Input from '~/components/commonUi/Input/Input';
import SelectText, { SelectTextProps } from '~/components/commonUi/SelectText/SelectText';
import TextArea from '~/components/commonUi/TextArea/TextArea';
import { REGION_FIELD_CONFIG, REGION_FIELD_DATA } from '~/consts/generalSettings';
import { Region } from '~/definitions/region';

interface Props {
    isSettingsDisabled: boolean;
    isSuperAdmin: boolean;
    regionName: string;
    regionDescription: string;
    regionExtraVoteLabel: string;
    regionExtraVoteQuantity: string;
    regionNoVoteDescription: string;
    regionNameError: boolean;
    regionExtraVoteQuantityError: boolean;
    selectedRegionData: Region;
    handleChangeRegionAttribute: (key: string, value: string) => void;
    setRegionName: (value: string) => void;
    setRegionDescription: (value: string) => void;
    setRegionExtraVoteLabel: (value: string) => void;
    setRegionExtraVoteQuantity: (value: string) => void;
    setRegionNoVoteDescription: (value: string) => void;
    setRegionNameError: (isError: boolean) => void;
    setRegionExtraVoteQuantityError: (isError: boolean) => void;
    updateRegionSettingsData: () => void;
}

const RegionSettingsDetails: FC<Props> = (props) => {
    const {
        isSettingsDisabled,
        isSuperAdmin,
        regionName,
        regionDescription,
        regionExtraVoteLabel,
        regionExtraVoteQuantity,
        regionNoVoteDescription,
        regionNameError,
        regionExtraVoteQuantityError,
        selectedRegionData,
        handleChangeRegionAttribute,
        setRegionName,
        setRegionDescription,
        setRegionExtraVoteLabel,
        setRegionExtraVoteQuantity,
        setRegionNoVoteDescription,
        setRegionNameError,
        setRegionExtraVoteQuantityError,
        updateRegionSettingsData,
    } = props;

    const getOptions = (): SelectTextProps['options'] => {
        let options: SelectTextProps['options'] = [];
        options =
            selectedRegionData?.regionExtraVoteLabel?.map((item) => {
                const { valueId = '', value = '' } = item;
                return {
                    value: valueId,
                    label: value,
                };
            }) || [];
        return options;
    };

    const region = selectedRegionData?.name || '';
    const title = `${region} innstillinger`;
    const fieldName = REGION_FIELD_DATA.regionName;
    const fieldDescription = REGION_FIELD_DATA.regionDescription;
    const fieldExtraVoteLabel = REGION_FIELD_DATA.regionExtraVoteLabel;
    const fieldExtraVoteQuantity = REGION_FIELD_DATA.regionExtraVoteQuantity;
    const fieldNoVoteDescription = REGION_FIELD_DATA.regionNoVoteDescription;
    const extraVoteOptions = getOptions();

    const rowClassName = 'grid grid-cols-12 mb-4 gap-4';
    const firstColClassName = 'col-span-3 text-left font-normal';
    const secondColClassName = 'col-span-9';
    const commonExtendedClassName = 'w-full';
    const errorClassNames = 'text-red-600';

    const handleChangeName = (value: string) => {
        setRegionName(value);
        value && setRegionNameError(false);
    };

    const handleChangeDescription = (value: string) => {
        setRegionDescription(value);
        handleChangeRegionAttribute(REGION_FIELD_CONFIG.regionDescription, value);
    };

    const handleChangeExtraVoteLabel = (value: string) => {
        setRegionExtraVoteLabel(value);
        handleChangeRegionAttribute(REGION_FIELD_CONFIG.regionExtraVoteLabel, value);
    };

    const handleChangeExtraVoteQuantity = (value: string) => {
        setRegionExtraVoteQuantity(value);
        (value.trim() !== '' || Number(value) >= 0) && setRegionExtraVoteQuantityError(false);
        handleChangeRegionAttribute(REGION_FIELD_CONFIG.regionExtraVoteQuantity, value);
    };

    const handleChangeNoVoteDescription = (value: string) => {
        setRegionNoVoteDescription(value);
        handleChangeRegionAttribute(REGION_FIELD_CONFIG.regionNoVoteDescription, value);
    };

    const handleSaveChanges = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        updateRegionSettingsData();
    };

    return (
        <Fieldset title={title}>
            <form
                action="settings"
                method="post"
                className={'frm-settings'}
                data-test-id="frm-settings"
                onSubmit={handleSaveChanges}
            >
                <div className="flex flex-col lg:flex-row gap-8 mb-4">
                    <div className="w-full lg:w-2/3">
                        <div className={rowClassName}>
                            <label
                                htmlFor={fieldName.id}
                                className={firstColClassName + ` ${regionNameError && errorClassNames}`}
                            >
                                {fieldName.label}
                                <span className="text-red-500">*</span>
                            </label>
                            <div className={secondColClassName}>
                                {isSuperAdmin ? (
                                    <Input
                                        disabled={isSettingsDisabled}
                                        id={fieldName.id}
                                        name={fieldName.name}
                                        placeholder={''}
                                        onChange={(event) => handleChangeName(event.target.value)}
                                        value={regionName}
                                        testId={fieldName.id}
                                        extendedClassName={commonExtendedClassName}
                                        error={regionNameError}
                                    />
                                ) : (
                                    <div className={secondColClassName}>{regionName}</div>
                                )}
                            </div>
                        </div>
                        <div className={rowClassName}>
                            <label htmlFor={fieldExtraVoteLabel.id} className={firstColClassName}>
                                {fieldExtraVoteLabel.label}
                            </label>
                            <div className={secondColClassName}>
                                <SelectText
                                    disabled={isSettingsDisabled}
                                    id={fieldExtraVoteLabel.id}
                                    name={fieldExtraVoteLabel.name}
                                    onChange={(event) => handleChangeExtraVoteLabel(event.target.value)}
                                    value={regionExtraVoteLabel}
                                    options={extraVoteOptions}
                                    testId={fieldExtraVoteLabel.id}
                                    extendedClassName={commonExtendedClassName}
                                />
                            </div>
                        </div>
                        <div className={rowClassName}>
                            <label htmlFor={fieldDescription.id} className={firstColClassName}>
                                {fieldDescription.label}
                            </label>
                            <div className={secondColClassName}>
                                <TextArea
                                    disabled={isSettingsDisabled}
                                    id={fieldDescription.id}
                                    name={fieldDescription.name}
                                    onChange={(event) => handleChangeDescription(event.target.value)}
                                    value={regionDescription}
                                    testId={fieldDescription.id}
                                    extendedClassName={`${commonExtendedClassName} h-[120px]`} // Old: min-height: calc(1.5em + .75rem + 2px);
                                />
                            </div>
                        </div>
                        <div className={rowClassName}>
                            <label
                                htmlFor={fieldExtraVoteQuantity.id}
                                className={firstColClassName + ` ${regionExtraVoteQuantityError && errorClassNames}`}
                            >
                                {fieldExtraVoteQuantity.label}
                                <span className="text-red-500">*</span>
                            </label>
                            <div className={secondColClassName}>
                                <Input
                                    disabled={isSettingsDisabled}
                                    id={fieldExtraVoteQuantity.id}
                                    name={fieldExtraVoteQuantity.name}
                                    placeholder={''}
                                    onChange={(event) => handleChangeExtraVoteQuantity(event.target.value)}
                                    value={regionExtraVoteQuantity}
                                    testId={fieldExtraVoteQuantity.id}
                                    type={'number'}
                                    extendedClassName={commonExtendedClassName}
                                    error={regionExtraVoteQuantityError}
                                />
                            </div>
                        </div>
                        <div className={rowClassName}>
                            <label htmlFor={fieldNoVoteDescription.id} className={firstColClassName}>
                                {fieldNoVoteDescription.label}
                            </label>
                            <div className={secondColClassName}>
                                <TextArea
                                    disabled={isSettingsDisabled}
                                    id={fieldNoVoteDescription.id}
                                    name={fieldNoVoteDescription.name}
                                    onChange={(event) => handleChangeNoVoteDescription(event.target.value)}
                                    value={regionNoVoteDescription}
                                    testId={fieldNoVoteDescription.id}
                                    extendedClassName={`${commonExtendedClassName} h-[120px]`} // Old: min-height: calc(1.5em + .75rem + 2px);
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="text-right mb-[16px]">
                    {!isSettingsDisabled && <Button type={'submit'} btnText={'Lagre'} testId={'save-button'} />}
                </div>
            </form>
        </Fieldset>
    );
};

export default RegionSettingsDetails;
